// GameLoadingScreen — fullscreen loader shown while a game's assets
// preload. Visually unique per game: the centre piece is that game's
// real Logo.png from /public/games/<id>/Logo.png — same artwork the
// player sees on the lobby, so the loader feels like the opening
// frame of the game, not a generic universal spinner.
//
// ════════════════════════════════════════════════════════════════════
// VISUAL CONTRACT
// ════════════════════════════════════════════════════════════════════
//
//   • Per-game background gradient that mirrors that game's lobby
//     palette (Ludo gold-on-purple, XO emerald, Chess walnut, Carrom
//     amber-wood, Rummy crimson). A SINGLE dedicated palette per game
//     — the loader does NOT follow the platform's light/dark toggle.
//     Earlier the loader swapped palettes on theme change; per user
//     feedback the dark variants read better across the board, so
//     they're now used unconditionally.
//
//   • Centred game logo with a gentle float (3 s loop, ±6 px). Logo
//     fades in once decoded so a cold preload doesn't flash a broken-
//     image icon before the PNG arrives.
//
//   • Thin circular progress ring around the logo. Two SVG circles:
//     a faint background track + a foreground arc whose
//     stroke-dashoffset tracks the [0..1] preload progress. The arc
//     uses a per-game gradient stroke so it ties back to the lobby.
//
//   • Soft theme-tinted glow halo behind the logo for depth.
//
//   • Sub-label below: short game name + "loading game…" + percentage.
//
// ════════════════════════════════════════════════════════════════════
// COMPONENT TREE
// ════════════════════════════════════════════════════════════════════
//
//   <GameLoadingScreen>            ← fixed-position root (z-[60])
//   ├─ <bg gradient div>           ← per-game gradient
//   ├─ <vignette + grain overlay>  ← soft polish
//   └─ <stack>
//      ├─ <halo glow>              ← radial blur behind logo
//      ├─ <SVG progress ring>      ← bg track + animated arc
//      ├─ <logo img>               ← /games/<id>/Logo.png
//      └─ <label + sublabel + %>

import { useEffect, useRef, useState } from 'react';

export type GameTheme = 'ludo' | 'xo' | 'chess' | 'carrom' | 'rummy' | 'snake_ladder' | 'lucky7' | 'thimbles';

type ThemeSpec = {
  label: string;
  logoUrl: string;
  // Inline-CSS gradient string. Used on the fullscreen background
  // <div>. We feed it as `style.background` rather than a Tailwind
  // class because the per-game palettes don't fit any reasonable
  // utility-class scheme.
  bg: string;
  // Ring + halo accent colour, expressed as CSS color strings so the
  // SVG gradient stops below can drop them in directly.
  accent:  string;          // primary stroke / glow colour
  accent2: string;          // secondary stop for the ring gradient
  // Glow halo behind the logo. Two-stop radial; the second stop is
  // always transparent of the same hue.
  halo:  string;
  // Text colour for the label + sublabel + percentage. Picked to
  // sit comfortably on top of the dark gradient.
  text:  string;
};

const THEMES: Record<GameTheme, ThemeSpec> = {
  ludo: {
    label:   'Ludo',
    logoUrl: '/games/ludo/Logo.png',
    bg:      'radial-gradient(ellipse at center, #4a126b 0%, #2a0845 55%, #100120 100%)',
    accent:  '#fde08b',
    accent2: '#d4af37',
    halo:    'radial-gradient(circle at center, rgba(253,224,139,.35) 0%, rgba(253,224,139,0) 70%)',
    text:    '#fff6d6',
  },
  xo: {
    label:   'XO',
    logoUrl: '/games/xo/Logo.png',
    bg:      'radial-gradient(ellipse at center, #115e59 0%, #0f3b3a 55%, #031418 100%)',
    accent:  '#5eead4',
    accent2: '#0f766e',
    halo:    'radial-gradient(circle at center, rgba(94,234,212,.30) 0%, rgba(94,234,212,0) 70%)',
    text:    '#a7f3d0',
  },
  chess: {
    label:   'Chess',
    logoUrl: '/games/chess/Logo.png',
    bg:      'radial-gradient(ellipse at center, #5a3f26 0%, #3b2815 55%, #1a0e05 100%)',
    accent:  '#d4b483',
    accent2: '#8b5e34',
    halo:    'radial-gradient(circle at center, rgba(212,180,131,.30) 0%, rgba(212,180,131,0) 70%)',
    text:    '#faf2e1',
  },
  carrom: {
    label:   'Carrom',
    logoUrl: '/games/carrom/Logo.png',
    // Navy + cyan to match the in-game carrom lobby
    // (radial-gradient(#0a1a47, #051640, #020617) in carrom.css).
    // The previous brown palette was a generic wood placeholder; the
    // real game theme is deep-blue with cyan accents.
    bg:      'radial-gradient(ellipse at center, #0a1a47 0%, #051640 55%, #020617 100%)',
    accent:  '#22d3ee',     // cyan-400, matches the lobby cyan glow
    accent2: '#0e7490',     // cyan-700, deeper stop for the ring gradient
    halo:    'radial-gradient(circle at center, rgba(34,211,238,.32) 0%, rgba(34,211,238,0) 70%)',
    text:    '#cffafe',     // cyan-100 — clean off-white on the navy
  },
  rummy: {
    label:   'Rummy',
    logoUrl: '/games/rummy/Logo.png',
    // Deep green felt + gold to match the in-game rummy table
    // (radial-gradient(#0a3b22, #052814, #010704) + #fde08b gold
    // throughout rummy.css). The previous crimson palette ignored
    // the actual casino-felt look.
    bg:      'radial-gradient(ellipse at center, #0e4a2d 0%, #052814 55%, #010704 100%)',
    accent:  '#fde08b',     // gold — same value used for rummy headings + buttons
    accent2: '#b88000',     // deeper gold stop for the ring gradient
    halo:    'radial-gradient(circle at center, rgba(253,224,139,.32) 0%, rgba(253,224,139,0) 70%)',
    text:    '#fde08b',     // gold text reads warmly on the green felt
  },
  snake_ladder: {
    label:   'Snake & Ladder',
    logoUrl: '/games/snake_ladder/Logo.png',
    // Same green radial + gold accents as the in-game SL lobby
    // (radial-gradient(#1a7a3a, #13602f, #06180e) + gold slim borders).
    bg:      'radial-gradient(ellipse at center, #1a7a3a 0%, #13602f 55%, #06180e 100%)',
    accent:  '#fde08b',     // gold ring — matches the gold logo + borders
    accent2: '#b88000',     // deeper gold stop for the ring gradient
    halo:    'radial-gradient(circle at center, rgba(253,224,139,.32) 0%, rgba(253,224,139,0) 70%)',
    text:    '#f8e7b3',     // warm cream-gold, readable on the green
  },
  lucky7: {
    label:   'Lucky 7',
    logoUrl: '/games/lucky7/Logo.png',
    // Deep crimson/red casino gradient to match the game theme
    bg:      'radial-gradient(ellipse at center, #60030b 0%, #2a0002 55%, #0d0000 100%)',
    accent:  '#ffe103',     // gold ring
    accent2: '#d68307',     // deeper gold stop for the ring gradient
    halo:    'radial-gradient(circle at center, rgba(255,225,3,.25) 0%, rgba(255,225,3,0) 70%)',
    text:    '#fde08b',     // gold text
  },
  thimbles: {
    label:   'Thimbles',
    logoUrl: '/games/thimbles/Logo.png',
    // Magenta house theme (≈ #971f71 / #892b6c) — same family as the in-game
    // stage + lobby tile — with gold accents framing the blue-velvet logo art.
    bg:      'radial-gradient(ellipse at center, #9c2274 0%, #5e1342 55%, #190510 100%)',
    accent:  '#ffd76a',     // gold ring — matches the cup highlights + win coin
    accent2: '#b9740d',     // deeper gold stop for the ring gradient
    halo:    'radial-gradient(circle at center, rgba(255,200,90,.30) 0%, rgba(255,200,90,0) 70%)',
    text:    '#f9dcec',     // warm cream-pink, readable on the magenta
  },
};

type Props = {
  /** When false the loader unmounts; when true it covers the screen. */
  visible: boolean;
  /** Which game's palette / label / logo to render. */
  theme: GameTheme;
  /** Clamped to [0..1]. Drives the ring fill + percentage. */
  progress: number;
  /** Optional override for the centred label (defaults to game name). */
  label?: string;
  /** Optional sub-text. Defaults to "loading game…". */
  subtext?: string;
};

// SVG geometry for the ring. r=72 gives a circumference of ~452 px;
// large enough to wrap a 144 px logo with comfortable breathing room.
const RING_RADIUS = 72;
const RING_CIRC   = 2 * Math.PI * RING_RADIUS;

export default function GameLoadingScreen({
  visible, theme, progress, label, subtext,
}: Props) {
  const t = THEMES[theme];

  // Hide the logo until decoded so a cold cache load doesn't flash a
  // broken-image icon before the PNG arrives. Resets every time the
  // loader becomes visible so a back/forward navigation can re-trigger
  // the fade-in.
  const [logoLoaded, setLogoLoaded] = useState(false);
  useEffect(() => { if (visible) setLogoLoaded(false); }, [visible, theme]);

  // Prevent body scroll while the loader is up — same trick the
  // recharge / withdrawal modals use. Restored on unmount.
  const prevOverflow = useRef<string>('');
  useEffect(() => {
    if (!visible) return;
    prevOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow.current; };
  }, [visible]);

  if (!visible) return null;

  const pct = Math.max(0, Math.min(1, progress));
  // Larger progress = smaller dashoffset (= more arc visible).
  const dashOffset = RING_CIRC * (1 - pct);
  const pctText = Math.round(pct * 100);

  return (
    <div
      role="status"
      aria-label={`Loading ${t.label} — ${pctText}%`}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center
                 select-none overflow-hidden"
      style={{ color: t.text, background: t.bg }}
    >
      {/* Soft vignette so the gradient feels framed rather than flat. */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.35) 100%)',
        }}
      />

      {/* Centerpiece: ring + halo + logo. Container is the layout
          anchor; everything inside is absolutely centred on top of
          everything else. */}
      <div className="relative w-[176px] h-[176px] flex items-center justify-center">

        {/* Glow halo — soft radial behind the logo, theme-tinted. */}
        <div
          aria-hidden
          className="absolute inset-[-20%] gls-halo"
          style={{ background: t.halo }}
        />

        {/* SVG progress ring. r=72, viewBox 0..200. Foreground arc is
            rotated -90° so the fill begins at 12 o'clock and sweeps
            clockwise as progress grows. Stroke gradient uses a linear
            stop pair so the arc looks like polished metal rather than
            a flat colour. */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 200 200"
          aria-hidden
        >
          <defs>
            <linearGradient id={`ring-grad-${theme}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor={t.accent}  />
              <stop offset="100%" stopColor={t.accent2} />
            </linearGradient>
          </defs>
          {/* Background track */}
          <circle
            cx="100" cy="100" r={RING_RADIUS}
            stroke={t.text} strokeOpacity={0.18}
            strokeWidth={3} fill="none"
          />
          {/* Foreground progress arc */}
          <circle
            cx="100" cy="100" r={RING_RADIUS}
            stroke={`url(#ring-grad-${theme})`}
            strokeWidth={5} fill="none"
            strokeLinecap="round"
            strokeDasharray={RING_CIRC}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 100 100)"
            style={{
              transition: 'stroke-dashoffset 0.3s ease-out',
              filter: `drop-shadow(0 0 8px ${t.accent})`,
            }}
          />
        </svg>

        {/* Game logo. Floats gently. Sits inside the ring with a small
            inset so the ring doesn't crowd the artwork. */}
        <img
          src={t.logoUrl}
          alt={t.label}
          draggable={false}
          onLoad={() => setLogoLoaded(true)}
          className="relative w-[112px] h-[112px] object-contain gls-float
                     transition-opacity duration-500"
          style={{ opacity: logoLoaded ? 1 : 0 }}
        />
      </div>

      {/* Label + sublabel + percentage. */}
      <div
        className="mt-8 font-display font-extrabold tracking-[.18em] uppercase"
        style={{ fontSize: '1.35rem' }}
      >
        {label || t.label}
      </div>
      <div className="text-[11px] tracking-[.32em] uppercase mt-1 opacity-70">
        {subtext || 'loading game…'}
      </div>
      <div
        className="mt-5 text-[10px] tracking-[.4em] tabular-nums opacity-55"
        style={{ color: t.text }}
      >
        {pctText}%
      </div>

      {/* Component-scoped keyframes. Prefixed `gls-` so we don't
          collide with any game's own CSS. */}
      <style>{`
        @keyframes gls-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .gls-float {
          animation: gls-float 3.2s ease-in-out infinite;
          filter: drop-shadow(0 8px 18px rgba(0,0,0,0.35));
        }
        @keyframes gls-halo-pulse {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%      { opacity: 1.0;  transform: scale(1.04); }
        }
        .gls-halo {
          animation: gls-halo-pulse 3.2s ease-in-out infinite;
          filter: blur(2px);
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
}
